import _ from 'lodash';
import React from 'react';
import {useDispatch} from 'react-redux';

import {useSelector} from '../../state/hooks';
import {getReportPart, getReportRef} from '../../state/reports/selectors';
import {setBlocks} from '../../state/views/report/actions';
import {useReportState} from './../../pages/ReportPage/store';
import {CompositionKeyboardContextProvider} from './CompositionKeyboardContext';
import {type WBSlateElementType} from './WBSlate/types';
import {WBSlate} from './WBSlate/WBSlate';
import {
  WBSlateReduxBridgeContext,
  type WBSlateReduxBridgeDebouncedProps,
} from './WBSlateReduxBridgeContext';

const WBSlateReduxBridgeDebounced: React.FC<WBSlateReduxBridgeDebouncedProps> =
  React.memo(({viewRef, view, projectName, entityName, viewId, readOnly}) => {
    const dispatch = useDispatch();
    const setIsSyncingToRedux = useReportState(
      state => state.setIsSyncingToRedux
    );
    const viewPartRef = useSelector(state => getReportRef(viewRef)(state));
    const viewPart = useSelector(state => getReportPart(viewRef)(state));
    const {blocks, panelSettingsRef, width} = viewPart;

    const reduxValue = React.useMemo(
      () => ({
        entityName,
        projectName,
        viewId,
        panelSettingsRef,
        viewRef,
        view,
      }),
      [entityName, projectName, viewId, panelSettingsRef, viewRef, view]
    );

    // oxlint-disable-next-line react-hooks/exhaustive-deps
    const updateRedux = React.useCallback(
      _.debounce((newValue: WBSlateElementType[]) => {
        if (!readOnly) {
          dispatch(setBlocks(viewPartRef, newValue));
          setIsSyncingToRedux(false);
        }
      }, 1000),
      [dispatch, viewPartRef]
    );

    const [value, setValue] = React.useState<WBSlateElementType[]>(blocks);
    const onChange = React.useCallback(
      (newValue: WBSlateElementType[]) => {
        // For some reason, this is fired whenever the selection changes.
        // Only update when the actual content changes.
        if (newValue !== value) {
          setValue(newValue);
          setIsSyncingToRedux(true);
          updateRedux(newValue);
        }
      },
      [value, updateRedux, setIsSyncingToRedux]
    );

    return (
      <WBSlateReduxBridgeContext.Provider value={reduxValue}>
        <CompositionKeyboardContextProvider>
          <WBSlate
            onChange={onChange}
            readOnly={readOnly}
            value={value}
            view={view}
            viewID={viewId}
            viewRef={viewRef}
            widthMode={width}
          />
        </CompositionKeyboardContextProvider>
      </WBSlateReduxBridgeContext.Provider>
    );
  });

export default WBSlateReduxBridgeDebounced;

export type WBSlateReduxBridgeDisabledProps = Pick<
  WBSlateReduxBridgeDebouncedProps,
  `entityName` | `projectName` | `viewId` | `readOnly`
> & {
  value: WBSlateElementType[];
  onChange: (newValue: WBSlateElementType[]) => void;
};

export const WBSlateReduxBridgeDisabled: React.FC<WBSlateReduxBridgeDisabledProps> =
  React.memo(({projectName, entityName, viewId, readOnly, value, onChange}) => {
    const reduxValue = React.useMemo(
      () => ({
        entityName,
        projectName,
        viewId,
        panelSettingsRef: null,
        viewRef: null,
        view: null,
      }),
      [entityName, projectName, viewId]
    );

    return (
      <WBSlateReduxBridgeContext.Provider value={reduxValue}>
        <CompositionKeyboardContextProvider>
          <WBSlate
            readOnly={readOnly}
            value={value}
            widthMode="profile"
            viewID={viewId}
            viewRef={null}
            view={null}
            onChange={onChange}
          />
        </CompositionKeyboardContextProvider>
      </WBSlateReduxBridgeContext.Provider>
    );
  });
